import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TagInput } from '../ui/tag-input';
import { SelectWithAdd } from '../ui/select-with-add';
import { DependentSelect } from '../ui/dependent-select';
import { ReviewPeriodSelect } from '../ui/review-period-select';
import { X, Save, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useMultipleCodelists } from '../../hooks/useCodelist';
import { useNis2Selection } from '../../hooks/useNis2Selection';
import { api } from '../../lib/api';
import { Asset, User } from '../../types';

interface AssetFormProps {
  asset?: Asset;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  mode: 'create' | 'edit' | 'view';
}

const AssetForm: React.FC<AssetFormProps> = ({
  asset,
  isOpen,
  onClose,
  onSave,
  mode
}) => {
  const [formData, setFormData] = useState<Asset>({
    name: '',
    description: '',
    asset_category: '',
    type: '',
    classification: 'primary',
    nis2_chapter: '', // Keep for backward compatibility
    nis2_article: '',
    nis2_letter: '',
    location: '',
    confidentiality_level: 'internal',
    integrity_level: 'medium',
    availability_level: 'medium',
    value_assessment: 'medium',
    dependencies: [],
    security_controls: [],
    review_date: '',
    review_period: '1y',
    status: 'active',
    tags: [],
    change_description: ''
  });

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'asset' | 'extended'>('asset');
  const { toast } = useToast();
  
  // Load codelists for display names
  const { getItemName } = useMultipleCodelists(['nis2_chapters', 'asset_types_nis2', 'security_controls']);
  
  // Handle NIS2 selection with proper auto-selection behavior
  const nis2Selection = useNis2Selection({
    initialArticle: formData.nis2_article || '',
    initialLetter: formData.nis2_letter || '',
    onSelectionChange: (selection) => {
      setFormData(prev => ({
        ...prev,
        nis2_article: selection.nis2_article,
        nis2_letter: selection.nis2_letter
      }));
    }
  });


  // Load users for owner/custodian selection
  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  // Initialize form with asset data when editing
  useEffect(() => {
    if (asset && isOpen) {
      setFormData({
        ...asset,
        name: asset.name || '',
        description: asset.description || '',
        asset_category: asset.asset_category || '',
        type: asset.type || '',
        classification: asset.classification || 'primary',
        nis2_chapter: asset.nis2_chapter || '', // Keep for backward compatibility
        nis2_article: asset.nis2_article || '',
        nis2_letter: asset.nis2_letter || '',
        location: asset.location || '',
        confidentiality_level: asset.confidentiality_level || 'internal',
        integrity_level: asset.integrity_level || 'medium',
        availability_level: asset.availability_level || 'medium',
        value_assessment: asset.value_assessment || 'medium',
        dependencies: asset.dependencies || [],
        security_controls: asset.security_controls || [],
        review_date: asset.review_date || '',
        review_period: asset.review_period || '1y',
        status: asset.status || 'active',
        tags: asset.tags || [],
        change_description: ''
      });
    } else if (isOpen && mode === 'create') {
      // Reset form for create mode
      setFormData({
        name: '',
        description: '',
        asset_category: '',
        type: '',
        classification: 'primary',
        nis2_chapter: '', // Keep for backward compatibility
        nis2_article: '',
        nis2_letter: '',
        location: '',
        confidentiality_level: 'internal',
        integrity_level: 'medium',
        availability_level: 'medium',
        value_assessment: 'medium',
        dependencies: [],
        security_controls: [],
        review_date: '',
        review_period: '1y',
        status: 'active',
        tags: [],
        change_description: ''
      });
    }
  }, [asset, isOpen, mode]);

  // Sync NIS2 selection hook with formData changes
  useEffect(() => {
    if (formData.nis2_article !== nis2Selection.article || formData.nis2_letter !== nis2Selection.letter) {
      nis2Selection.setSelection({
        nis2_article: formData.nis2_article || '',
        nis2_letter: formData.nis2_letter || ''
      });
    }
  }, [formData.nis2_article, formData.nis2_letter, nis2Selection]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Use the correct API endpoint structure
      const response = await api.get<{ users: User[]; pagination: any }>('/admin/users');
      console.log('Users API response:', response);
      setUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      // Try alternative endpoint
      try {
        const altResponse = await api.getAllUsers() as { users: User[]; pagination: any };
        console.log('Alternative users API response:', altResponse);
        setUsers(altResponse.users || []);
      } catch (altError) {
        console.error('Alternative users API also failed:', altError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'view') return;

    try {
      setSaving(true);
      
      // Review date and period will be handled by backend auto-calculation
      
      if (mode === 'create') {
        // Filter out read-only fields for create
        const createData = {
          name: formData.name,
          description: formData.description,
          asset_category: formData.asset_category,
          type: formData.type,
          classification: formData.classification,
          nis2_chapter: formData.nis2_chapter,
          nis2_article: formData.nis2_article,
          nis2_letter: formData.nis2_letter,
          owner_id: formData.owner_id,
          custodian_id: formData.custodian_id,
          location: formData.location,
          confidentiality_level: formData.confidentiality_level,
          integrity_level: formData.integrity_level,
          availability_level: formData.availability_level,
          value_assessment: formData.value_assessment,
          dependencies: formData.dependencies,
          security_controls: formData.security_controls,
          review_date: formData.review_date || undefined,
          review_period: formData.review_period,
          status: formData.status,
          tags: formData.tags
        };
        
        // Filter out undefined/empty values but keep required fields
        const filteredCreateData = Object.fromEntries(
          Object.entries(createData).filter(([key, value]) => {
            // Always keep required fields and important defaults
            if (['name', 'classification', 'review_period'].includes(key)) {
              return true;
            }
            // Filter out empty optional fields
            return value !== undefined && value !== '' && 
                   !(Array.isArray(value) && value.length === 0);
          })
        );
        
        console.log('Creating asset with data:', filteredCreateData);
        const response = await api.post<{ success: boolean; data: Asset }>('/assets', filteredCreateData);
        if (response.success) {
          toast({
            title: "Success",
            description: "Asset created successfully",
          });
          onSave();
          onClose();
        }
      } else if (mode === 'edit' && asset?.id) {
        // Filter out read-only fields and null/undefined values for update
        const updateData: any = {};
        
        // Only include fields that have values (not null, undefined, or empty string)
        if (formData.name?.trim()) updateData.name = formData.name.trim();
        if (formData.description?.trim()) updateData.description = formData.description.trim();
        if (formData.asset_category) updateData.asset_category = formData.asset_category;
        if (formData.type) updateData.type = formData.type;
        if (formData.classification) updateData.classification = formData.classification;
        if (formData.nis2_chapter?.trim()) updateData.nis2_chapter = formData.nis2_chapter.trim();
        if (formData.nis2_article?.trim()) updateData.nis2_article = formData.nis2_article.trim();
        if (formData.nis2_letter?.trim()) updateData.nis2_letter = formData.nis2_letter.trim();
        if (formData.owner_id && formData.owner_id > 0) updateData.owner_id = formData.owner_id;
        if (formData.custodian_id && formData.custodian_id > 0) updateData.custodian_id = formData.custodian_id;
        if (formData.location?.trim()) updateData.location = formData.location.trim();
        if (formData.confidentiality_level) updateData.confidentiality_level = formData.confidentiality_level;
        if (formData.integrity_level) updateData.integrity_level = formData.integrity_level;
        if (formData.availability_level) updateData.availability_level = formData.availability_level;
        if (formData.value_assessment) updateData.value_assessment = formData.value_assessment;
        if (formData.dependencies?.length > 0) updateData.dependencies = formData.dependencies;
        if (formData.security_controls?.length > 0) updateData.security_controls = formData.security_controls;
        // Include review date and period for backend auto-calculation
        if (formData.review_date?.trim()) updateData.review_date = formData.review_date.trim();
        if (formData.review_period) updateData.review_period = formData.review_period;
        if (formData.status) updateData.status = formData.status;
        if (formData.tags?.length > 0) updateData.tags = formData.tags;
        if (formData.change_description?.trim()) updateData.change_description = formData.change_description.trim();
        
        console.log('Updating asset with filtered data:', updateData);
        const response = await api.put<{ success: boolean; data: Asset }>(`/assets/${asset.id}`, updateData);
        if (response.success) {
          toast({
            title: "Success",
            description: "Asset updated successfully",
          });
          onSave();
          onClose();
        }
      }
    } catch (error: any) {
      console.error('Failed to save asset:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save asset",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>
              {mode === 'create' ? 'Create Asset' : mode === 'edit' ? 'Edit Asset' : 'Asset Details'}
            </CardTitle>
            <CardDescription>
              {mode === 'create' 
                ? 'Add a new information asset to your inventory'
                : mode === 'edit' 
                ? 'Update asset information and settings'
                : 'View asset details and history'
              }
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        {/* Tabs - only show if asset has extended data */}
        {asset?.csv_extended_data && (
          <div className="border-b">
            <div className="flex space-x-8 px-6">
              <button
                type="button"
                onClick={() => setActiveTab('asset')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'asset'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Asset
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('extended')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'extended'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Extended Data
              </button>
            </div>
          </div>
        )}

        <CardContent>
          {/* Asset Tab Content */}
          {activeTab === 'asset' && (
            <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Asset Name *</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Main Database Server"
                  required
                  disabled={mode === 'view'}
                />
              </div>

              <div className="md:col-span-2">
                <DependentSelect
                  label="Asset Category & Type"
                  parentValue={formData.asset_category || ''}
                  childValue={formData.type || ''}
                        onParentChange={(value) => setFormData(prev => ({ ...prev, asset_category: Array.isArray(value) ? value[0] || '' : value }))}
                        onChildChange={(value) => setFormData(prev => ({ ...prev, type: Array.isArray(value) ? value[0] || '' : value }))}
                  parentCodelistType="asset_categories"
                  childCodelistType="asset_types_nis2"
                  parentLabel="Asset Category"
                  childLabel="Asset Type"
                  required={true}
                  disabled={mode === 'view'}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="classification">Classification *</Label>
                <select
                  id="classification"
                  value={formData.classification || 'primary'}
                  onChange={(e) => setFormData(prev => ({ ...prev, classification: e.target.value as Asset['classification'] }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  required
                  disabled={mode === 'view'}
                >
                  <option value="primary">Primary</option>
                  <option value="supporting">Supporting</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={formData.status || 'active'}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as Asset['status'] }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  disabled={mode === 'view'}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description of the asset"
                className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm min-h-[80px]"
                disabled={mode === 'view'}
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Data Center A, Office Building"
                disabled={mode === 'view'}
              />
            </div>

            {/* NIS2 Article & Letter - Full width for better readability */}
            <div className="space-y-2">
              <DependentSelect
                label="NIS2 Article & Letter"
                parentValue={nis2Selection.article}
                childValue={nis2Selection.letter}
                onParentChange={nis2Selection.handleParentChange}
                onChildChange={nis2Selection.handleChildChange}
                parentCodelistType="nis2_articles"
                childCodelistType="nis2_letters"
                placeholder="Select NIS2 Article & Letter..."
                disabled={mode === 'view'}
                preserveChildOnAutoSelect={true}
              />
            </div>

            {/* Ownership */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="owner_id">Asset Owner</Label>
                <select
                  id="owner_id"
                  value={formData.owner_id || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner_id: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  disabled={mode === 'view' || loading}
                >
                  <option value="">Select owner...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="custodian_id">Asset Custodian</Label>
                <select
                  id="custodian_id"
                  value={formData.custodian_id || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, custodian_id: e.target.value ? parseInt(e.target.value) : undefined }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  disabled={mode === 'view' || loading}
                >
                  <option value="">Select custodian...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* CIA Classification */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">CIA Classification</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="confidentiality_level">Confidentiality</Label>
                  <select
                    id="confidentiality_level"
                    value={formData.confidentiality_level || 'internal'}
                    onChange={(e) => setFormData(prev => ({ ...prev, confidentiality_level: e.target.value as Asset['confidentiality_level'] }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                    disabled={mode === 'view'}
                  >
                    <option value="public">Public</option>
                    <option value="internal">Internal</option>
                    <option value="confidential">Confidential</option>
                    <option value="restricted">Restricted</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="integrity_level">Integrity</Label>
                  <select
                    id="integrity_level"
                    value={formData.integrity_level || 'medium'}
                    onChange={(e) => setFormData(prev => ({ ...prev, integrity_level: e.target.value as Asset['integrity_level'] }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                    disabled={mode === 'view'}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="availability_level">Availability</Label>
                  <select
                    id="availability_level"
                    value={formData.availability_level || 'medium'}
                    onChange={(e) => setFormData(prev => ({ ...prev, availability_level: e.target.value as Asset['availability_level'] }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                    disabled={mode === 'view'}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Value Assessment and Review */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="value_assessment">Value Assessment</Label>
                <select
                  id="value_assessment"
                  value={formData.value_assessment || 'medium'}
                  onChange={(e) => setFormData(prev => ({ ...prev, value_assessment: e.target.value as Asset['value_assessment'] }))}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  disabled={mode === 'view'}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <ReviewPeriodSelect
                value={formData.review_period || '1y'}
                onChange={(value) => setFormData(prev => ({ ...prev, review_period: value }))}
                label="Review Period"
                disabled={mode === 'view'}
                showPreview={true}
                currentReviewDate={formData.review_date}
              />

              <div className="space-y-2">
                <Label htmlFor="review_date">
                  Next Review Date
                  <span className="text-xs text-muted-foreground ml-2">
                    (optional - calculated automatically)
                  </span>
                </Label>
                <Input
                  id="review_date"
                  type="date"
                  value={formData.review_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, review_date: e.target.value }))}
                  disabled={mode === 'view'}
                  placeholder="Auto-calculated from review period"
                />
              </div>
            </div>

            {/* Dependencies and Security Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TagInput
                  tags={formData.dependencies || []}
                  onTagsChange={(deps) => setFormData(prev => ({ ...prev, dependencies: deps }))}
                  label="Dependencies"
                  placeholder="e.g., database, network"
                  disabled={mode === 'view'}
                />
              </div>

              <div>
                <SelectWithAdd
                  label="Security Controls"
                  value={formData.security_controls || []}
                  onChange={(value) => setFormData(prev => ({ ...prev, security_controls: value as string[] }))}
                  codelistType="security_controls"
                  placeholder="Select security controls..."
                  disabled={mode === 'view'}
                  onAddItem={async (item) => {
                    await api.post('/codelists/items', {
                      codelist_type: 'security_controls',
                      code: item.code,
                      name: item.name,
                      description: item.description
                    });
                  }}
                />
              </div>
            </div>

            {/* Tags */}
            <TagInput
              tags={formData.tags || []}
              onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
              label="Tags"
              placeholder="e.g., production, critical"
              disabled={mode === 'view'}
            />

            {/* Change Description (for edits) */}
            {mode === 'edit' && (
              <div className="space-y-2">
                <Label htmlFor="change_description">Change Description</Label>
                <Input
                  id="change_description"
                  value={formData.change_description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, change_description: e.target.value }))}
                  placeholder="Describe what changes you made"
                />
              </div>
            )}

            {/* Current Values Display (for view mode) */}
            {mode === 'view' && asset && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Current Values</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Type</span>
                        <Badge variant="outline" className="ml-2">{getItemName('asset_types_nis2', formData.type || '')}</Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Confidentiality</span>
                    <Badge variant="outline" className="ml-2">{formData.confidentiality_level}</Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Integrity</span>
                    <Badge variant="outline" className="ml-2">{formData.integrity_level}</Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Availability</span>
                    <Badge variant="outline" className="ml-2">{formData.availability_level}</Badge>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Value</span>
                    <Badge variant="outline" className="ml-2">{formData.value_assessment}</Badge>
                  </div>
                  {formData.nis2_chapter && (
                    <div>
                      <span className="text-sm text-muted-foreground">NIS2 Chapter</span>
                      <Badge variant="outline" className="ml-2">{getItemName('nis2_chapters', formData.nis2_chapter)}</Badge>
                    </div>
                  )}
                </div>

                {formData.dependencies.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Dependencies:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.dependencies.map((dep, index) => (
                        <Badge key={index} variant="secondary">{dep}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {formData.security_controls.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Security Controls:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.security_controls.map((control, index) => (
                        <Badge key={index} variant="secondary">{getItemName('security_controls', control)}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {formData.tags.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.tags.map((tag, index) => (
                        <Badge key={index} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                {mode !== 'view' && (
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <Save className="h-4 w-4 mr-2" />
                    {mode === 'create' ? 'Create Asset' : 'Update Asset'}
                  </Button>
                )}
              </div>
            </form>
          )}

          {/* Extended Data Tab Content */}
          {activeTab === 'extended' && asset?.csv_extended_data && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Extended Data from CSV Import</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Data imported from CSV file: <code className="bg-gray-100 px-1 rounded">{asset.csv_import_filename}</code>
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Field
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(asset.csv_extended_data).map(([key, value]) => (
                      <tr key={key}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                          {key}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {value || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetForm;
